import { useState } from "react";
import {
  FormContainer,
  FormHeader,
  FormTitle,
  FormDescription,
  InputField,
  InputIcon,
  InputContent
} from "./styles";

const UserIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#626262"
    strokeWidth="2"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const PhoneIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#626262"
    strokeWidth="2"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const CustomCheckbox = ({ checked, onChange }) => (
  <div
    onClick={onChange}
    style={{
      width: "20px",
      height: "20px",
      border: "2px solid #d1d5db",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      backgroundColor: checked ? "#000000" : "#ffffff",
      borderColor: checked ? "#000000" : "#d1d5db",
      transition: "all 0.2s ease",
      flexShrink: 0
    }}
  >
    {checked && (
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <circle cx="5" cy="5" r="3" fill="white" />
      </svg>
    )}
  </div>
);

const RugbyWc25Form = ({ onSubmit, onCancel, savingPersonalDetails }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    acceptTerms: false
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    let formErrors = {};

    const namePattern = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/;
    if (!formData.fullName.trim()) {
      formErrors.fullName = "Full name is required";
    } else if (!namePattern.test(formData.fullName)) {
      formErrors.fullName = "Please enter a valid name";
    }

    const phonePattern = /^[+]?[1-9][\d]{0,15}$/;
    if (!formData.phoneNumber.trim()) {
      formErrors.phoneNumber = "Phone number is required";
    } else if (!phonePattern.test(formData.phoneNumber.replace(/\s/g, ""))) {
      formErrors.phoneNumber = "Please enter a valid phone number";
    }

    if (!formData.acceptTerms) {
      formErrors.terms = "You must accept the terms and conditions";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <FormContainer>
      <div className="inner-container">
        <form onSubmit={handleSubmit}>
          <FormHeader>
            <FormTitle>Enter Details</FormTitle>
            <FormDescription>
              Please provide your details to view your PIN and balance. It will
              only be requested once.
            </FormDescription>
          </FormHeader>

          <div className="inputs">
            <InputField>
              <InputIcon>
                <UserIcon />
              </InputIcon>
              <InputContent>
                <input
                  type="text"
                  placeholder="Full name"
                  value={formData.fullName}
                  onChange={(e) =>
                    handleInputChange("fullName", e.target.value)
                  }
                  required
                />
              </InputContent>
            </InputField>
            {errors.fullName && <div className="error">{errors.fullName}</div>}

            <InputField>
              <InputIcon>
                <PhoneIcon />
              </InputIcon>
              <InputContent>
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    handleInputChange("phoneNumber", e.target.value)
                  }
                  required
                />
              </InputContent>
            </InputField>
            {errors.phoneNumber && (
              <div className="error">{errors.phoneNumber}</div>
            )}
          </div>

          <div className="terms">
            <CustomCheckbox
              checked={formData.acceptTerms}
              onChange={() =>
                handleInputChange("acceptTerms", !formData.acceptTerms)
              }
            />
            <p>
              I accept the{" "}
              <a
                href="https://www.bobofin.com/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </a>
              .
            </p>
          </div>
          {errors.terms && (
            <div
              className="error"
              style={{ textAlign: "center", marginTop: "-16px" }}
            >
              {errors.terms}
            </div>
          )}

          <div className="button-group">
            <button
              type="submit"
              className="primary"
              disabled={savingPersonalDetails}
            >
              {savingPersonalDetails ? (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid transparent",
                      borderTop: "2px solid #ffffff",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      marginRight: "8px"
                    }}
                  />
                  SAVING...
                </span>
              ) : (
                "VIEW PIN"
              )}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="secondary"
              disabled={savingPersonalDetails}
            >
              CANCEL
            </button>
          </div>
        </form>
      </div>
    </FormContainer>
  );
};

export default RugbyWc25Form;
